import type { ConfigChange, ConfigChangeActorKind, ConfigChangeArea } from '../types.js'

/** What each area is called on the page. */
export const AREA_LABEL: Record<ConfigChangeArea, string> = {
  facility_config: 'Facility settings',
  facility: 'Facility details',
  behaviors: 'Behaviors',
  add_on_license: 'Add-on license',
  add_on_switch: 'Add-on switch',
  holiday_events: 'Holiday events',
  client: 'Client registration',
  admin: 'Admin login',
  operator: 'Operator login',
  facility_administrator: 'Facility administrator login',
  worker_login: 'Worker login',
  change_request: 'Change requests',
  invitation: 'Invitations',
}

export const AREAS = Object.keys(AREA_LABEL) as ConfigChangeArea[]

export const ACTOR_KIND_LABEL: Record<ConfigChangeActorKind, string> = {
  root: 'Root admin',
  admin: 'Admin',
  sales: 'Sales',
  operator: 'Operator',
  facility_administrator: 'Facility administrator',
  worker: 'Worker',
  partner: 'Partner system',
  reset_link: 'Password reset link',
  invitation: 'Invitation accepted',
  anonymous: 'Unidentified request',
  system: 'System',
}

const ACTION_VERB = { created: 'created', updated: 'changed', deleted: 'removed' } as const

/** "Olive Operator", or what else names the actor: their login, or what they are. */
export function actorName(change: Pick<ConfigChange, 'actor'>): string {
  return change.actor.name ?? change.actor.id ?? ACTOR_KIND_LABEL[change.actor.kind]
}

/** "Facility settings changed", "Operator login created". */
export function headline(change: Pick<ConfigChange, 'area' | 'action'>): string {
  return `${AREA_LABEL[change.area]} ${ACTION_VERB[change.action]}`
}

/** A stored value as one line: a dash for none, text as written, anything else as compact JSON. */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—'
  }
  if (typeof value === 'string') {
    return value === '' ? '(empty)' : value
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return JSON.stringify(value)
}

/** "2026-10-17" for the local day an instant falls on, the key the list is grouped by. */
export function localDay(iso: string): string {
  const date = new Date(iso)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
